import { z } from 'zod';
import { createCheckoutAttemptId } from '../../utils/checkoutAttemptId';
import { GOOGLE_ADS_CONVERSION_ID, GOOGLE_ADS_LEAD_LABEL, pushEvent } from '../../utils/analytics';
import { getChallengeExperimentEventProperties } from './challengePageExperiment';
import type { ChallengeExperimentContext } from './challengePageExperiment';

/** What this module hands the Google tag (gtag.js). */
type GoogleAdsUserData = { email?: string; phone_number?: string };
type GoogleAdsConversionParams = {
  send_to: string;
  value: number;
  currency: 'USD';
  transaction_id?: string;
  /** gtag calls this after processing the event; this is not a receipt. */
  event_callback?: () => void;
};

declare global {
  interface Window {
    gtag?: {
      (command: 'set', target: 'user_data', params: GoogleAdsUserData): void;
      (command: 'set', params: { user_data: null }): void;
      (
        command: 'consent',
        target: 'update',
        params: { ad_user_data: 'denied'; ad_personalization: 'denied' },
      ): void;
      (command: 'event', target: 'conversion', params: GoogleAdsConversionParams): void;
    };
  }
}

/**
 * The two tap questions asked right after a free challenge seat is saved, and
 * the separate provisional qualification value (Rob, revised 2026-10-04).
 * EVERY number lives in CHALLENGE_LEAD_VALUE_MODEL
 * below; update it there and nowhere else.
 *
 * Value = average course price ($2,000) x base buy rate (6.06%)
 *         x lift(how long investing) x lift(spent on courses), whole dollars.
 * A question not answered has lift 1.0 (the average). Each lift is the buy
 * rate of people who gave that answer divided by the question's base rate,
 * from old ActiveCampaign applicants 2020-2024. Sources:
 *   MarketingManager/docs/investigations/2026-09-30_challenge-page-test-plan.md
 *   /Users/robertgrzesik/Development/active_campaign_notes_extract/all.csv
 * Examples: nothing answered $121; Never + $0 $18; 10+ years + over $20,000
 * $670.
 *
 * What Google Ads hears (Rob, 2026-10-02, final; browser only):
 *   1. Valid registration submit (step 1): the existing "Lead (Manual - Web)"
 *      conversion, exactly as trackConversion sent it before Sep 30, 2026
 *      (value 0, USD), with an opaque stable browser submission id for retries,
 *      before awaiting the server. See sendChallengeSeatLead.
 *   2. The tap that answers question 2: a separate "Challenge Lead with
 *      qualifying answers" conversion with the value below, once, before the
 *      server save resolves or calendar loads, using the dedicated action label.
 * Nothing is sent for stopping early, closing the page or going idle, and the
 * server sends nothing. Node stores the answers and the value
 * (botspot_node challengeQualifyingAnswers.controller.ts) and keeps no copy of
 * these numbers.
 */

// Historical lifetime-education lifts are provisional for the current challenge audience.
export const CHALLENGE_QUESTION_VERSION = 'online_education_lifetime_v1';
// Exact questions in active_campaign_notes_extract/all.csv. Keep the meaning
// aligned with the historical answer bands used by the provisional model.
export const CHALLENGE_QUESTION_TEXT = {
  investingExperience:
    'How long have you been investing? (in either stocks, bonds, options, crypto, or FOREX)',
  courseSpend: 'How much money have you invested into online education in your lifetime?',
} as const;
export const CHALLENGE_LEAD_VALUE_MODEL_VERSION = 'legacy_course_lifts_provisional_v1';

export const CHALLENGE_INVESTING_EXPERIENCE_OPTIONS = [
  { value: 'never', label: 'Never' },
  { value: '1_year_or_less', label: '1 year or less' },
  { value: '1_to_3_years', label: '1 to 3 years' },
  { value: '3_to_10_years', label: '3 to 10 years' },
  { value: '10_plus_years', label: '10+ years' },
] as const;

export const CHALLENGE_COURSE_SPEND_OPTIONS = [
  { value: '0', label: '$0' },
  { value: 'under_1000', label: 'Under $1,000' },
  { value: '1000_to_5000', label: '$1,000 to $5,000' },
  { value: '5000_to_20000', label: '$5,000 to $20,000' },
  { value: 'over_20000', label: 'Over $20,000' },
] as const;

export type ChallengeInvestingExperience =
  (typeof CHALLENGE_INVESTING_EXPERIENCE_OPTIONS)[number]['value'];
export type ChallengeCourseSpend = (typeof CHALLENGE_COURSE_SPEND_OPTIONS)[number]['value'];

export const CHALLENGE_LEAD_VALUE_MODEL = {
  averageCoursePriceUsd: 2000,
  baseBuyRate: 0.0606,
  investingExperienceLift: {
    never: 0.33,
    '1_year_or_less': 0.52,
    '1_to_3_years': 1.53,
    '3_to_10_years': 1.41,
    '10_plus_years': 2.82,
  },
  courseSpendLift: {
    '0': 0.45,
    under_1000: 0.49,
    '1000_to_5000': 1.53,
    '5000_to_20000': 1.82,
    over_20000: 1.96,
  },
} as const satisfies {
  averageCoursePriceUsd: number;
  baseBuyRate: number;
  investingExperienceLift: Record<ChallengeInvestingExperience, number>;
  courseSpendLift: Record<ChallengeCourseSpend, number>;
};

const INVESTING_VALUES: ReadonlySet<string> = new Set(
  CHALLENGE_INVESTING_EXPERIENCE_OPTIONS.map((option) => option.value),
);
const SPEND_VALUES: ReadonlySet<string> = new Set(
  CHALLENGE_COURSE_SPEND_OPTIONS.map((option) => option.value),
);

export const isChallengeInvestingExperience = (
  value: string | null | undefined,
): value is ChallengeInvestingExperience => Boolean(value) && INVESTING_VALUES.has(String(value));

export const isChallengeCourseSpend = (
  value: string | null | undefined,
): value is ChallengeCourseSpend => Boolean(value) && SPEND_VALUES.has(String(value));

export type ChallengeQualifyingAnswers = {
  investingExperience?: string | null;
  courseSpend?: string | null;
};

/** Whole US dollars. A missing or unknown answer counts as average (lift 1). */
export function challengeQualifyingLeadValue({
  investingExperience,
  courseSpend,
}: ChallengeQualifyingAnswers): number {
  const model = CHALLENGE_LEAD_VALUE_MODEL;
  const investingLift = isChallengeInvestingExperience(investingExperience)
    ? model.investingExperienceLift[investingExperience]
    : 1;
  const spendLift = isChallengeCourseSpend(courseSpend) ? model.courseSpendLift[courseSpend] : 1;
  return Math.round(model.averageCoursePriceUsd * model.baseBuyRate * investingLift * spendLift);
}

/**
 * Google wants E.164 for enhanced conversions. Only the shapes the challenge
 * form accepts are converted; anything else is left out rather than guessed.
 */
export function toGoogleAdsPhoneNumber(phone: string | null | undefined): string | undefined {
  const raw = String(phone || '').trim();
  if (!raw) return undefined;
  const digits = raw.replace(/\D/g, '');
  if (raw.startsWith('+')) return /^[1-9][0-9]{7,14}$/.test(digits) ? `+${digits}` : undefined;
  if (/^[2-9][0-9]{9}$/.test(digits)) return `+1${digits}`;
  if (/^1[2-9][0-9]{9}$/.test(digits)) return `+${digits}`;
  return undefined;
}

const SEAT_LEAD_KEY_PREFIX = 'botspot.challengeSeatLeadSent.';
const QUALIFIED_LEAD_KEY_PREFIX = 'botspot.challengeQualifiedLeadSent.';
const sentThisPage = new Set<string>();

/** Test hook: forget in-memory sends (sessionStorage stays, as on a reload). */
export const resetChallengeQualifiedLeadMemory = (): void => {
  sentThisPage.clear();
  submissionIds.clear();
};

const submissionIds = new Map<string, string>();
const SUBMISSION_IDS_KEY = 'botspot.challengeLeadSubmissionIds';
const submissionIdsSchema = z.record(z.string(), z.string());

/** An opaque browser submission id survives retries before Node returns a registration id. */
export function getChallengeLeadSubmissionId(registrationScope: string, email: string): string {
  const key = JSON.stringify([registrationScope, email.trim().toLowerCase()]);
  const remembered = submissionIds.get(key);
  if (remembered) return remembered;
  let stored: Record<string, string> = {};
  try {
    const parsed = submissionIdsSchema.safeParse(
      JSON.parse(window.sessionStorage.getItem(SUBMISSION_IDS_KEY) || '{}'),
    );
    if (parsed.success) stored = parsed.data;
  } catch {
    /* Optional storage never prevents acquisition tracking. */
  }
  const id = stored[key] || createCheckoutAttemptId('challenge_submission');
  submissionIds.set(key, id);
  try {
    window.sessionStorage.setItem(SUBMISSION_IDS_KEY, JSON.stringify({ ...stored, [key]: id }));
  } catch {
    /* The page-level id still protects retries. */
  }
  return id;
}

const alreadySent = (key: string): boolean => {
  if (sentThisPage.has(key)) return true;
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

/** A prior successful tag invocation remains true after a failed save/retry. */
export const wasChallengeQualifiedLeadDispatched = (registrationId: string): boolean =>
  alreadySent(QUALIFIED_LEAD_KEY_PREFIX + registrationId);

const rememberSent = (key: string): void => {
  sentThisPage.add(key);
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // In-memory memory still stops a double send on this page.
  }
};

/**
 * The send_to of the "Challenge Lead with qualifying answers" conversion:
 * Google Ads action 7814716469 (one per click, value from the page), created
 * by Rob on 2026-10-02. REACT_APP_GOOGLE_ADS_CHALLENGE_QUALIFIED_LEAD_SEND_TO
 * overrides it; an empty or invalid send_to sends nothing.
 */
export const CHALLENGE_QUALIFIED_LEAD_SEND_TO = 'AW-10985667985/2rOzCLW4rI4dEJH7r_Yo';

export function challengeQualifiedLeadSendTo(): string {
  const sendTo = String(
    process.env.REACT_APP_GOOGLE_ADS_CHALLENGE_QUALIFIED_LEAD_SEND_TO ||
      CHALLENGE_QUALIFIED_LEAD_SEND_TO,
  ).trim();
  // A bare numeric action id is not a label (Google drops it silently), and
  // this must never be the existing Lead.
  if (!/^AW-\d+\/[A-Za-z0-9_-]+$/.test(sendTo) || /\/\d+$/.test(sendTo)) return '';
  if (sendTo === `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_LEAD_LABEL}`) return '';
  return sendTo;
}

/**
 * How long the page waits for gtag to report the qualified lead sent before it
 * moves on to the calendar anyway. A blocked or slow tag never strands anyone.
 */
export const CHALLENGE_LEAD_SEND_CONFIRM_MS = 1000;

const consentModeEntrySchema = z.object({
  0: z.literal('consent'),
  1: z.enum(['default', 'update']),
  2: z.object({
    ad_user_data: z.enum(['granted', 'denied']).optional(),
    ad_personalization: z.enum(['granted', 'denied']).optional(),
  }),
});
export function getChallengeGoogleAdsConsent() {
  type ConsentStatus = 'CONSENT_GRANTED' | 'CONSENT_DENIED' | 'CONSENT_STATUS_UNSPECIFIED';
  let adUserData: ConsentStatus = 'CONSENT_STATUS_UNSPECIFIED';
  let adPersonalization: ConsentStatus = 'CONSENT_STATUS_UNSPECIFIED';
  let source: 'google_consent_mode' | 'global_privacy_control' | 'not_specified' = 'not_specified';
  // SAFETY: Optional dataLayer is validated as documented consent tuples below.
  const { dataLayer = [] } = window as Window & { dataLayer?: unknown[] };
  for (const entry of dataLayer) {
    // gtag stores Arguments; GTM integrations may store arrays. Normalize
    // only this documented consent tuple and validate its actual enum values.
    const parsed = consentModeEntrySchema.safeParse(Object.assign({}, entry));
    if (!parsed.success) continue;
    const state = parsed.data[2];
    if (state.ad_user_data)
      adUserData = state.ad_user_data === 'granted' ? 'CONSENT_GRANTED' : 'CONSENT_DENIED';
    if (state.ad_personalization)
      adPersonalization =
        state.ad_personalization === 'granted' ? 'CONSENT_GRANTED' : 'CONSENT_DENIED';
    source = 'google_consent_mode';
  }
  // SAFETY: GPC is an optional standardized navigator boolean, never a consent grant.
  if ((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true) {
    adUserData = 'CONSENT_DENIED';
    adPersonalization = 'CONSENT_DENIED';
    source = 'global_privacy_control';
  }
  return { adUserData, adPersonalization, source, capturedAt: new Date().toISOString() };
}

const setGoogleAdsUserData = (
  gtag: NonNullable<Window['gtag']>,
  email: string | null | undefined,
  phone: string | null | undefined,
): void => {
  if (getChallengeGoogleAdsConsent().adUserData !== 'CONSENT_GRANTED') return;
  const userData: GoogleAdsUserData = {};
  const normalizedEmail = String(email || '')
    .trim()
    .toLowerCase();
  if (normalizedEmail) userData.email = normalizedEmail;
  const phoneNumber = toGoogleAdsPhoneNumber(phone);
  if (phoneNumber) userData.phone_number = phoneNumber;
  if (userData.email || userData.phone_number) gtag('set', 'user_data', userData);
};

const reconcileGoogleAdsConsent = (gtag: NonNullable<Window['gtag']>): void => {
  const consent = getChallengeGoogleAdsConsent();
  if (consent.source === 'global_privacy_control') {
    gtag('consent', 'update', { ad_user_data: 'denied', ad_personalization: 'denied' });
  }
  // User data is global to the tag. Clear an earlier enhanced-conversion
  // identity before this event when no current permission allows its use.
  if (consent.adUserData !== 'CONSENT_GRANTED') gtag('set', { user_data: null });
};

export type ChallengeSeatLeadInput = {
  registrationId: string | null | undefined;
  email?: string | null;
  phone?: string | null;
};

/**
 * Step 1, valid submit: the site's existing Google Ads Lead (the same one the
 * sales page fires), exactly as trackConversion sent it before Sep 30, 2026:
 * `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_LEAD_LABEL}`, value 0, USD. It
 * adds transaction_id = the opaque browser submission id, so Google counts a retry
 * once; without an id it goes out as before, with none. Once per registration
 * (memory and sessionStorage).
 *
 * The email and phone go to the tag as user data first. gtag normalizes and
 * SHA-256 hashes them in the browser (enhanced conversions), so later server
 * conversions can match people with no click id; the plain values never leave.
 */
export function sendChallengeSeatLead(input: ChallengeSeatLeadInput): boolean {
  const { gtag } = window;
  if (!gtag || !GOOGLE_ADS_CONVERSION_ID || !GOOGLE_ADS_LEAD_LABEL) return false;
  const registrationId = input.registrationId || null;
  const key = registrationId ? SEAT_LEAD_KEY_PREFIX + registrationId : null;
  if (key && alreadySent(key)) return false;
  try {
    reconcileGoogleAdsConsent(gtag);
    setGoogleAdsUserData(gtag, input.email, input.phone);
    gtag('event', 'conversion', {
      send_to: `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_LEAD_LABEL}`,
      value: 0,
      currency: 'USD',
      ...(registrationId && { transaction_id: registrationId }),
    });
    if (key) rememberSent(key);
    return true;
  } catch {
    return false;
  }
}

export type ChallengeQualifiedLeadInput = ChallengeQualifyingAnswers & {
  registrationId: string | null | undefined;
  /** Called exactly once: when the hit is sent, after the safety wait, or at once if nothing is sent. */
  onSent?: () => void;
  /** Defaults to challengeQualifiedLeadSendTo(). */
  sendTo?: string;
};

export type ChallengeQualifiedLeadResult = { fired: boolean; value: number };

/**
 * The tap that answers question 2: the separate "Challenge Lead with
 * qualifying answers" conversion, with the value, USD and transaction_id =
 * registration id, once per registration. Only with both answers and a
 * configured send_to. `onSent` runs once gtag reports the hit sent, or after
 * CHALLENGE_LEAD_SEND_CONFIRM_MS, or at once when nothing is sent.
 */
export function sendChallengeQualifiedLeadConversion(
  input: ChallengeQualifiedLeadInput,
): ChallengeQualifiedLeadResult {
  const value = challengeQualifyingLeadValue(input);
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    input.onSent?.();
  };
  const notSent = (): ChallengeQualifiedLeadResult => {
    finish();
    return { fired: false, value };
  };
  const sendTo = (input.sendTo ?? challengeQualifiedLeadSendTo()).trim();
  const { registrationId } = input;
  if (!sendTo || !registrationId) return notSent();
  if (
    !isChallengeInvestingExperience(input.investingExperience) ||
    !isChallengeCourseSpend(input.courseSpend)
  ) {
    return notSent();
  }
  const { gtag } = window;
  if (!gtag) return notSent();
  const key = QUALIFIED_LEAD_KEY_PREFIX + registrationId;
  if (alreadySent(key)) return notSent();
  try {
    reconcileGoogleAdsConsent(gtag);
    window.setTimeout(finish, CHALLENGE_LEAD_SEND_CONFIRM_MS);
    gtag('event', 'conversion', {
      send_to: sendTo,
      value,
      currency: 'USD',
      transaction_id: registrationId,
      event_callback: finish,
    });
    rememberSent(key);
    return { fired: true, value };
  } catch {
    return notSent();
  }
}

/**
 * Internal analytics for every seat, sent alongside the one Lead: PostHog
 * receives `challenge_qualifying_answered` with the answers so far, the value
 * and what triggered it, plus the experiment arm.
 */
export function trackChallengeQualifyingAnswered({
  investingExperience,
  courseSpend,
  value,
  trigger,
  googleAdsLeadSent,
  experimentContext,
}: ChallengeQualifyingAnswers & {
  value: number;
  trigger: 'answered';
  googleAdsLeadSent: boolean;
  experimentContext?: ChallengeExperimentContext;
}): void {
  pushEvent({
    ...getChallengeExperimentEventProperties(experimentContext),
    event: 'challenge_qualifying_answered',
    qualification_question_version: CHALLENGE_QUESTION_VERSION,
    lead_value_model_version: CHALLENGE_LEAD_VALUE_MODEL_VERSION,
    lead_value_status: 'provisional',
    source: window.location.pathname,
    investing_experience: isChallengeInvestingExperience(investingExperience)
      ? investingExperience
      : 'not_answered',
    course_spend: isChallengeCourseSpend(courseSpend) ? courseSpend : 'not_answered',
    lead_value: value,
    lead_value_currency: 'USD',
    lead_trigger: trigger,
    google_ads_lead_sent: googleAdsLeadSent,
  });
}
